import { useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  FormControl,
  TextField,
  createFilterOptions,
} from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import CarSelectRenderOption from "components/cars/CarSelectRenderOption";
import useCarSelectReference, {
  CarStateOption,
  CarSource,
} from "hooks/cars/useCarSelectReference";
import { useAppSelector } from "redux/hooks";
import { selectReferenceCar } from "redux/slices/shed/shedSlice";
import { omit } from "lodash/fp";

type Props = {
  size: "small";
  inputValue: string;
  onSearchCar: (licensePlate: string) => void;
  onInputChange: (newInputValue: string) => void;
  carSource?: CarSource;
  /** Compact control: selected car summary is the dropdown trigger. */
  embedded?: boolean;
};

const filterOptions = createFilterOptions<CarStateOption>({
  matchFrom: "any",
  stringify: (option) => `${option.carBrand ?? ""} ${option.carModel ?? ""}`,
});

const CarSelectReference = ({
  onSearchCar,
  size,
  inputValue,
  onInputChange,
  carSource = "default",
  embedded = false,
}: Props) => {
  const [open, setOpen] = useState(false);
  const referenceCar = useAppSelector(selectReferenceCar);
  const { handleAddSearchOption, carOptions, handleSelection, handleBlur } =
    useCarSelectReference({
      inputValue,
      onSearchCar,
      carSource,
    });

  const selectedOption = useMemo(() => {
    if (!referenceCar) {
      return null;
    }
    return (
      carOptions.find(
        (option) =>
          (referenceCar.genericVin &&
            option.genericVin === referenceCar.genericVin) ||
          (referenceCar.licensePlate &&
            option.licensePlate === referenceCar.licensePlate) ||
          (option.carBrand === referenceCar.carBrand &&
            option.carModel === referenceCar.carModel)
      ) ?? null
    );
  }, [carOptions, referenceCar]);

  const hasSelectedCar = Boolean(
    referenceCar?.carBrand || referenceCar?.carModel
  );
  const showSummary = embedded && hasSelectedCar && !inputValue && !open;

  return (
    <Box display="flex" flexWrap="wrap" width="100%">
      <FormControl fullWidth variant="standard">
        <Autocomplete
          id="car-reference-selector"
          open={open}
          onOpen={() => setOpen(true)}
          onClose={() => {
            setOpen(false);
            onInputChange("");
          }}
          options={carOptions}
          value={selectedOption}
          onChange={(event, value) => {
            handleSelection(event, value);
            setOpen(false);
            onInputChange("");
          }}
          onBlur={handleBlur}
          getOptionLabel={(car: CarStateOption) =>
            `${car.carBrand ?? ""} ${car.carModel ?? ""}`.trim()
          }
          isOptionEqualToValue={(option, value) =>
            Boolean(
              (option.genericVin &&
                value.genericVin &&
                option.genericVin === value.genericVin) ||
                (option.licensePlate &&
                  value.licensePlate &&
                  option.licensePlate === value.licensePlate) ||
                (option.carBrand === value.carBrand &&
                  option.carModel === value.carModel)
            )
          }
          renderOption={(
            renderProps,
            {
              genericVin,
              carBrand,
              emissionCO2,
              carModel,
              licensePlate,
              inputValue: optionInputValue,
            }: CarStateOption
          ) => (
            <Box
              key={genericVin ? genericVin : licensePlate}
              component="li"
              {...omit(["key"])(renderProps)}
            >
              <CarSelectRenderOption
                carBrand={carBrand ?? ""}
                emissionCO2={emissionCO2 ?? 0}
                carModel={carModel ?? ""}
                licensePlate={licensePlate ?? ""}
                inputValue={optionInputValue ?? ""}
              />
            </Box>
          )}
          filterOptions={(options: CarStateOption[], params) =>
            handleAddSearchOption(
              filterOptions(options, params),
              params.inputValue
            )
          }
          inputValue={inputValue}
          onInputChange={(_event, newInputValue, reason) => {
            if (reason === "reset") {
              return;
            }
            onInputChange(newInputValue);
          }}
          popupIcon={<ArrowDropDownIcon />}
          forcePopupIcon
          sx={
            embedded
              ? {
                  width: "100%",
                  "& .MuiOutlinedInput-root": {
                    py: 0.5,
                    px: 0.75,
                    bgcolor: "rgba(200, 230, 201, 0.55)",
                    borderRadius: 1,
                    border: "1px solid",
                    borderColor: "rgba(129, 199, 132, 0.55)",
                    "& fieldset": { border: "none" },
                  },
                  "& .MuiAutocomplete-endAdornment": {
                    right: 4,
                  },
                }
              : undefined
          }
          renderInput={(params) =>
            embedded ? (
              <TextField
                {...params}
                placeholder={showSummary ? undefined : "Select a car"}
                variant="outlined"
                size={size}
                autoComplete="off"
                inputProps={{
                  ...params.inputProps,
                  ...(showSummary
                    ? {
                        style: {
                          ...((params.inputProps as { style?: object })
                            ?.style ?? {}),
                          opacity: 0,
                          width: 0,
                          minWidth: 0,
                          padding: 0,
                          position: "absolute" as const,
                        },
                      }
                    : {}),
                }}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: showSummary ? (
                    <Box
                      flex={1}
                      minWidth={0}
                      pr={3}
                      sx={{ pointerEvents: "none" }}
                    >
                      <CarSelectRenderOption
                        carBrand={referenceCar?.carBrand ?? ""}
                        carModel={referenceCar?.carModel ?? ""}
                        emissionCO2={referenceCar?.emissionCO2 ?? 0}
                        licensePlate={referenceCar?.licensePlate ?? ""}
                        inputValue=""
                      />
                    </Box>
                  ) : (
                    params.InputProps.startAdornment
                  ),
                }}
              />
            ) : (
              <TextField
                {...params}
                label="Select a car"
                variant="standard"
                size={size}
                autoComplete="off"
              />
            )
          }
        />
      </FormControl>
    </Box>
  );
};

export default CarSelectReference;
